/**
 * Pure control-flow for the Virtual Staging "wide capture": briefly reshape the
 * render to a target aspect (e.g. 16/9) at the SAME vertical FOV, let it settle,
 * grab the frame, then ALWAYS restore. Extracted from createViewer so the
 * sequence + save/restore + width math are unit-testable without a GPU. The
 * PlayCanvas primitives are injected via `deps`.
 */

export interface WideCaptureCameraState {
    aspectRatioMode: number;
    aspectRatio: number;
    horizontalFov: boolean;
}

export interface WideCaptureDeps {
    /** Desired output aspect (e.g. 16/9). Must be > 0 to reshape; otherwise falls back. */
    targetAspect: number;
    /** Current backbuffer height; the wide width is round(height * targetAspect). */
    canvasHeight: number;
    /** Mutable camera state (read for restore, written to reshape). Null → fallback grab. */
    camera: WideCaptureCameraState | null;
    /** pc.ASPECT_AUTO — the camera follows the reshaped backbuffer. */
    aspectAuto: number;
    /** pc.RESOLUTION_FIXED — pin the backbuffer to the target aspect. */
    resolutionFixed: string;
    /** pc.RESOLUTION_AUTO — restore normal auto-sizing. */
    resolutionAuto: string;
    /** False when the app can't switch resolution (→ fallback grab at the current aspect). */
    canReshape: boolean;
    setResolution(mode: string, width?: number, height?: number): void;
    resizeCanvas(): void;
    /** Request one rendered frame (app.renderNextFrame = true). */
    requestRender(): void;
    /** Resolve once a rendered frame has been presented (postrender + rAF). */
    settleFrame(): Promise<void>;
    /** Grab the current backbuffer as an image blob. */
    grab(): Promise<Blob>;
}

/** Render+settle cycles before the grab, so the resized backbuffer + any
 *  post-processing targets reconcile before pixels are read. */
export const WIDE_CAPTURE_SETTLE_FRAMES = 2;

/** Output width for a wide capture. Falls back to a 1080-tall reference when the
 *  live backbuffer height is unavailable (0 / NaN). */
export function wideCaptureWidth(canvasHeight: number, targetAspect: number): number {
    const height = Math.max(1, Math.round(canvasHeight || 1080));
    return Math.max(1, Math.round(height * targetAspect));
}

export async function runWideCapture(deps: WideCaptureDeps): Promise<Blob> {
    const { camera, targetAspect } = deps;

    // Fallback: no camera, invalid aspect, or can't reshape → grab at the current aspect.
    if (!camera || !(targetAspect > 0) || !deps.canReshape) {
        await deps.settleFrame();
        return deps.grab();
    }

    const prev: WideCaptureCameraState = {
        aspectRatioMode: camera.aspectRatioMode,
        aspectRatio: camera.aspectRatio,
        horizontalFov: camera.horizontalFov,
    };
    try {
        const height = Math.max(1, Math.round(deps.canvasHeight || 1080));
        const width = wideCaptureWidth(deps.canvasHeight, targetAspect);
        camera.horizontalFov = false;          // keep the VERTICAL FOV constant
        camera.aspectRatioMode = deps.aspectAuto; // follow the reshaped backbuffer
        deps.setResolution(deps.resolutionFixed, width, height);
        deps.resizeCanvas();
        for (let i = 0; i < WIDE_CAPTURE_SETTLE_FRAMES; i++) {
            deps.requestRender();
            await deps.settleFrame();
        }
        return await deps.grab();
    } finally {
        camera.aspectRatioMode = prev.aspectRatioMode;
        camera.aspectRatio = prev.aspectRatio;
        camera.horizontalFov = prev.horizontalFov;
        deps.setResolution(deps.resolutionAuto);
        deps.resizeCanvas();
        deps.requestRender();
    }
}
